import { GIFT_REGISTRY_WIDGET_NAMES } from "@/constants/widget-names";
import {
    globalProductIdManagerToken,
    globalProductVariantsIdManagerToken,
} from "@/tokens";
import { AddToRegistryProductPageWidgetDefinition } from "@apps/gift_registry/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import { ProductVariantIdFromStaticProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromStaticProvider";
import { ProductVariantIdFromUrlProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromUrlProvider";
import { ProductVariantsIdManager } from "@lib/ProductVariantIdManager/ProductVariantIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";

const BUTTON_CLASS_DATA_ATTR = "gw-button-class";
const PRODUCT_ID_DATA_ATTR = "gw-product-id";
const VARIANT_ID_DATA_ATTR = "gw-variant-id";

class AddToRegistryProductPageWidgetInjector {
    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {}

    start() {
        this.logger.debug("AddToRegistryProductPageWidgetInjector.start", {
            placeholderSelector: this.placeholderSelector,
        });

        const targets$ = this.moi.register({
            targetSelector: this.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.logger.debug(
                "AddToRegistryProductPageWidgetInjector found placeholder",
                { placeholder }
            );

            const widgetName =
                GIFT_REGISTRY_WIDGET_NAMES.ADD_TO_REGISTRY_PRODUCT_PAGE_WIDGET;

            const buttonClass = parseStringDataAttr(
                placeholder,
                BUTTON_CLASS_DATA_ATTR
            );

            const productIdFromDataAttr = parseIntDataAttr(
                placeholder,
                PRODUCT_ID_DATA_ATTR
            );

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            const productVariantIdFromDataAttr = parseIntDataAttr(
                placeholder,
                VARIANT_ID_DATA_ATTR
            );

            const productVariantsIdManager =
                productVariantIdFromDataAttr === null
                    ? container.resolve(globalProductVariantsIdManagerToken)
                    : new ProductVariantsIdManager();

            if (productVariantIdFromDataAttr !== null) {
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromStaticProvider(
                        productVariantIdFromDataAttr
                    )
                );
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromUrlProvider()
                );
            }

            void (async () => {
                const AddToRegistryProductPageWidget =
                    await this.dynamicImportManager.importDefault(
                        AddToRegistryProductPageWidgetDefinition,
                        true
                    );

                const widget = new AddToRegistryProductPageWidget(
                    {
                        target: placeholder,
                        buttonClass: buttonClass || "",
                    },
                    this.logger,
                    this.modalManager,
                    productIdManager,
                    productVariantsIdManager
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default AddToRegistryProductPageWidgetInjector;
